// Application des critères de filtres catalogue (#305 / #341) au formulaire
// `#listing-filter-form`.
//
// Mutualisé entre :
// - `profiles_controller.js` (panneau « Mes profils », #290/#341) qui restaure
//   les filtres du profil nouvellement actif lors d'un changement de profil ;
// - `profile_wizard_controller.js` (wizard « Nouveau profil », #307/#424) qui
//   doit réinitialiser ces mêmes filtres aux défauts du profil créé, faute de
//   quoi les valeurs stales du profil précédent (orientation, étage, prix, tri…)
//   restent affichées — et peuvent être auto-sauvegardées — sur le nouveau
//   profil.
//
// Ces champs (localisation, nombre de pièces, prix, orientation, étage, tri) ne
// vivent pas dans `#edit-profile-form` mais dans `#listing-filter-form`, ils
// doivent donc être resynchronisés à part.

import { setCriteriaField, cssEscape } from "./criteria_fields";

// Applique l'intégralité des critères de filtres catalogue au formulaire
// `#listing-filter-form` : localisation (+ champs cachés lat/lng/region/
// department/city), nombre de pièces, prix du bien, orientation, étage et tri.
export function applyFiltersForm(criterias) {
    const form = document.getElementById("listing-filter-form");
    if (!form || !criterias) return;

    [
        "invest_location",
        "invest_location_lat",
        "invest_location_lng",
        "invest_region",
        "invest_department",
        "invest_city_id",
    ].forEach((key) => setCriteriaField(form, key, criterias[key]));

    applyNumberOfRooms(form, criterias.invest_number_of_rooms);
    applyPrice(form, criterias.invest_budget_min, criterias.invest_budget_max);
    applyCheckboxFilter(form, "invest_expositions", criterias.invest_expositions);
    applyCheckboxFilter(form, "invest_floors", criterias.invest_floors);
    applySort(form, criterias.sort, criterias.sort_direction);
}

// Restaure le filtre « Prix du bien » (#341) : champs cachés
// `criterias[invest_budget_min/max]` + rafraîchissement du slider jQuery UI
// (`PriceSlider`, `app/assets/javascripts/price_slider.js`).
export function applyPrice(form, min, max) {
    const minField = form.querySelector('input[name="criterias[invest_budget_min]"]');
    const maxField = form.querySelector('input[name="criterias[invest_budget_max]"]');
    if (minField) minField.value = min == null ? "" : String(min);
    if (maxField) maxField.value = max == null ? "" : String(max);

    refreshPriceSlider(form);
}

// Repositionne le slider de prix et ses libellés sur les valeurs des champs
// cachés. Le slider est piloté par jQuery UI (asset pipeline) : on l'ajuste
// sans réécrire les champs cachés (une valeur vide = pas de filtre, le
// slider retombe alors sur ses bornes pour l'affichage).
export function refreshPriceSlider(form) {
    try {
        const jquery = window.jQuery || window.$;
        if (!jquery) return;

        const range = jquery(form).find(".slider-range");
        if (range.length === 0 || typeof range.slider !== "function") return;

        const rawMin = range.find(".invest_budget_min").val();
        const rawMax = range.find(".invest_budget_max").val();
        const low = rawMin === "" || rawMin == null ? range.data("min") : parseInt(rawMin, 10);
        const high = rawMax === "" || rawMax == null ? range.data("max") : parseInt(rawMax, 10);

        range.slider("values", [low, high]);
        if (window.PriceSlider && typeof window.PriceSlider.setPriceLabels === "function") {
            window.PriceSlider.setPriceLabels(low, high);
        }
    } catch (_) {}
}

// Restaure un filtre à choix multiple rendu sous forme de cases à cocher
// (`criterias[<key>][]`) : orientation et étage (#341). Le profil stocke
// « toutes les valeurs » (ex. ['N','S','E','O']) pour signifier « pas de
// filtre » ; côté UI cela se traduit par « aucune case cochée ». On
// resynchronise l'état coché ET la classe `active` des labels Bootstrap
// (gérés sinon par `button-toggle-input`).
export function applyCheckboxFilter(form, key, values) {
    const checkboxes = Array.from(
        form.querySelectorAll(
            `input[type="checkbox"][name="${cssEscape(`criterias[${key}][]`)}"]`
        )
    );
    if (checkboxes.length === 0) return;

    const available = checkboxes.map((checkbox) => String(checkbox.value));
    const selected = Array.isArray(values) ? values.map((value) => String(value)) : [];
    const coversAll =
        selected.length > 0 && available.every((value) => selected.includes(value));
    const effective = coversAll ? [] : selected;

    checkboxes.forEach((checkbox) => {
        const checked = effective.includes(String(checkbox.value));
        checkbox.checked = checked;
        const label = checkbox.closest("label");
        if (label) label.classList.toggle("active", checked);
    });
}

// Restaure le tri du catalogue (#341) : le select `criterias[sort]` et les
// radios `criterias[sort_direction]` (avec leurs classes `active`).
export function applySort(form, sort, direction) {
    const select = form.querySelector('select[name="criterias[sort]"]');
    if (select && sort != null) select.value = String(sort);

    const radios = Array.from(
        form.querySelectorAll('input[type="radio"][name="criterias[sort_direction]"]')
    );
    radios.forEach((radio) => {
        const checked = String(radio.value) === String(direction);
        radio.checked = checked;
        const label = radio.closest("label");
        if (label) label.classList.toggle("active", checked);
    });
}

// Le profil stocke une seule valeur `invest_number_of_rooms`, tandis que le
// filtre est rendu sous forme de cases à cocher
// (`criterias[invest_number_of_rooms][]`). La case « 4 » signifie « 4 pièces
// et plus ». On resynchronise l'état coché ET la classe `active` des labels
// Bootstrap (gérés sinon par `button-toggle-input`).
export function applyNumberOfRooms(form, value) {
    const checkboxes = Array.from(
        form.querySelectorAll('input[type="checkbox"][name="criterias[invest_number_of_rooms][]"]')
    );
    if (checkboxes.length === 0) return;

    const selected = value == null || value === "" ? null : parseInt(value, 10);
    checkboxes.forEach((checkbox) => {
        const rooms = parseInt(checkbox.value, 10);
        const checked =
            selected != null && (rooms === 4 ? selected >= 4 : rooms === selected);
        checkbox.checked = checked;
        const label = checkbox.closest("label");
        if (label) label.classList.toggle("active", checked);
    });
}
